/**
 * ECOPD-5566：把外界給的語系代碼收斂成介面真的有翻譯的那三種
 * （= `src/i18n.ts` 的 `resources` key）。
 *
 * 語系代碼有兩個來源，**兩者都不保證乾淨**：
 *   - `navigator.language`：帶地區碼、甚至帶書寫系統 —— `en-US`、`ja-JP`、`zh-Hant-TW`。
 *   - cookie `language`：可能是舊版直接把上面那些原始值寫進去的，或後端風格的 `zh_TW`。
 *
 * 這些值直接餵進 i18next 會有兩種下場：
 *   1. `en-US`、`ja-JP` 這種 i18next 查表時自己解得掉（`en-US` → `en`），但
 *      `i18n.language` **仍是原字串**，於是全站二十幾個直接比對／索引 `i18n.language`
 *      的地方一起錯：`nameI18n['en-US']` 是 undefined、`i18n.language === 'ja'` 是 false、
 *      送給後端的 `accept-language` 是 `en_us`。而且全都無聲無息。
 *   2. `zh-Hant-TW` 連 i18next 也解不掉 —— 它的 fallback 階層是
 *      `zh-Hant-TW` → `zh-Hant` → `zh`，三個都沒有 resource，整包落到
 *      `fallbackLng: 'en'`，台灣使用者整個介面變英文。
 *
 * 所以在**進入 i18next 之前**就收斂：開機語系（`src/i18n.ts`）與
 * `CookieUserDefaults.language` 都過這裡，`i18n.language` 之後只會是這三個值，
 * 上面那些使用點不必各自防禦。
 */
export const SUPPORTED_LANGUAGES = ['en', 'zh-TW', 'ja'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * 認不出來的語系給什麼。
 *
 * ⚠️ 必須跟 `src/i18n.ts` 的 `fallbackLng` 一致，**不要改成 `zh-TW`**：
 * 法文使用者今天看到的就是英文介面，改成 zh-TW 會讓他們突然看到中文。
 */
export const FALLBACK_LANGUAGE: SupportedLanguage = 'en';

/**
 * 判定「繁體中文」的 subtag。
 *
 * ⚠️ 只認繁體：`zh-Hans` / `zh-CN` / `zh-SG`（簡體）刻意落到 `FALLBACK_LANGUAGE`，
 * 維持它們今天就是英文介面的行為 —— 我們只有繁體語系檔，要不要拿繁體給簡體使用者
 * 是另一個產品決定，不在這裡偷偷做。單獨的 `zh`（沒書寫系統也沒地區）在 CLDR 的
 * 預設書寫系統是簡體，同樣不認。
 */
const TRADITIONAL_CHINESE_SUBTAGS = ['hant', 'tw', 'hk', 'mo'];

/**
 * `en-US` → `en`、`ja-JP` → `ja`、`zh-Hant-TW` / `zh_TW` / `zh-hk` → `zh-TW`，
 * 其餘 → `FALLBACK_LANGUAGE`。空字串 / undefined 也走 fallback。
 */
export function normalizeLanguage(code?: string | null): SupportedLanguage {
  // 分隔符兩種都收：BCP 47 的 `-` 與後端／cookie 舊值的 `_`（`zh_TW`）。
  const [base, ...subtags] = (code ?? '')
    .toLowerCase()
    .split(/[-_]/)
    .filter(Boolean);
  switch (base) {
    case 'en':
      return 'en';
    case 'ja':
      return 'ja';
    case 'zh':
      return subtags.some((subtag) =>
        TRADITIONAL_CHINESE_SUBTAGS.includes(subtag)
      )
        ? 'zh-TW'
        : FALLBACK_LANGUAGE;
    default:
      return FALLBACK_LANGUAGE;
  }
}
